import React from 'react';
import { Box, Typography, Stack, LinearProgress } from '@mui/material';
import { styled } from '@mui/material/styles';

const TaskContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: '32px',
  alignItems: 'flex-start',
  [theme.breakpoints.down('lg')]: {
    gap: '24px'
  },
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
    gap: '24px',
    width: '100%'
  }
}));

const ProgressCircleWrapper = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '140px',
  height: '140px',
  flexShrink: 0,
  [theme.breakpoints.down('sm')]: {
    width: '120px',
    height: '120px',
    alignSelf: 'center'
  }
}));

const ProgressCircleSvg = styled('svg')({
  transform: 'rotate(-90deg)',
  width: '100%',
  height: '100%'
});

const CircleBackground = styled('circle')({
  fill: 'none',
  stroke: '#e2e8f0',
  strokeWidth: 10
});

const CircleProgress = styled('circle')(({ percentage }) => ({
  fill: 'none',
  stroke: 'url(#progressGradient)',
  strokeWidth: 10,
  strokeLinecap: 'round',
  strokeDasharray: `${(percentage / 100) * 339.292} 339.292`,
  transition: 'stroke-dasharray 0.8s ease-in-out'
}));

const CircleCenterContent = styled(Box)({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  textAlign: 'center'
});

const PercentageText = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '32px',
  fontWeight: 700,
  color: '#1a1a2e',
  lineHeight: 1,
  [theme.breakpoints.down('sm')]: {
    fontSize: '28px'
  }
}));

const CompletedText = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '12px',
  fontWeight: 500,
  color: '#64748b',
  marginTop: '4px'
}));

const TaskList = styled(Stack)(({ theme }) => ({
  gap: '16px',
  width: '100%',
  flex: 1
}));

const TaskItem = styled(Box)(({ theme }) => ({
  background: '#f8fafc',
  borderRadius: '12px',
  padding: '14px 16px',
  transition: 'all 0.2s ease',
  '&:hover': {
    background: '#f1f5f9',
    transform: 'translateX(4px)'
  }
}));

const TaskRow = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: '12px',
  marginBottom: '10px'
}));

const TaskName = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '14px',
  fontWeight: 600,
  color: '#1a1a2e',
  flex: 1,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap'
}));

const TaskRatio = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  fontFamily: 'Inter, sans-serif',
  fontSize: '13px',
  fontWeight: 600,
  color: '#64748b'
}));

const TaskRatioNumber = styled('span')(({ theme, isCompleted }) => ({
  color: isCompleted ? '#22c55e' : '#3b82f6',
  fontWeight: 700
}));

const StyledLinearProgress = styled(LinearProgress)(({ theme, value }) => ({
  height: '6px',
  borderRadius: '3px',
  backgroundColor: '#e2e8f0',
  '& .MuiLinearProgress-bar': {
    background: value === 100 
      ? 'linear-gradient(90deg, #22c55e 0%, #16a34a 100%)'
      : 'linear-gradient(90deg, #3b82f6 0%, #8b5cf6 100%)',
    borderRadius: '3px'
  }
}));

const EmptyState = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '24px',
  background: '#f8fafc',
  borderRadius: '12px',
  textAlign: 'center'
}));

const EmptyIcon = styled(Box)({
  fontSize: '32px',
  marginBottom: '12px'
});

const EmptyText = styled(Typography)({
  fontFamily: 'Inter, sans-serif',
  fontSize: '14px',
  color: '#64748b'
});

const TaskCompletionWidget = React.memo(({ percentage, tasks, isRealData, totalTasks, completedTasks }) => {
  const progressValue = Math.min(Math.max(percentage || 0, 0), 100);
  
  return (
    <TaskContainer>
      <ProgressCircleWrapper>
        <ProgressCircleSvg viewBox="0 0 120 120">
          <defs>
            <linearGradient id="progressGradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#22c55e" />
              <stop offset="100%" stopColor="#16a34a" />
            </linearGradient>
          </defs>
          <CircleBackground cx="60" cy="60" r="54" />
          <CircleProgress cx="60" cy="60" r="54" percentage={progressValue} />
        </ProgressCircleSvg>
        <CircleCenterContent>
          <PercentageText>{progressValue}%</PercentageText>
          <CompletedText>
            {isRealData ? `${completedTasks || 0}/${totalTasks || 0}` : 'Complete'}
          </CompletedText>
        </CircleCenterContent>
      </ProgressCircleWrapper>
      
      <TaskList>
        {tasks && tasks.length > 0 ? tasks.map((task, index) => {
          const taskProgress = task.total > 0 ? (task.completed / task.total) * 100 : 0;
          const isCompleted = task.completed === task.total && task.total > 0;
          
          return (
            <TaskItem key={index}>
              <TaskRow>
                <TaskName>{task.name}</TaskName>
                <TaskRatio>
                  <TaskRatioNumber isCompleted={isCompleted}>{task.completed}</TaskRatioNumber>
                  <span>/</span>
                  <span>{task.total}</span>
                </TaskRatio>
              </TaskRow>
              <StyledLinearProgress 
                variant="determinate" 
                value={taskProgress}
              />
            </TaskItem>
          );
        }) : (
          <EmptyState>
            <EmptyIcon>📋</EmptyIcon>
            <EmptyText>No tasks assigned yet</EmptyText>
          </EmptyState>
        )}
      </TaskList>
    </TaskContainer>
  );
});

TaskCompletionWidget.displayName = 'TaskCompletionWidget';

export default TaskCompletionWidget;
